import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { ForkKnife, Plus, Trash, FloppyDisk, ArrowLeft } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const DEFAULT_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((n) => ({
  name: n,
  meals: [
    { slot: "Breakfast", title: "", notes: "" },
    { slot: "Lunch", title: "", notes: "" },
    { slot: "Dinner", title: "", notes: "" },
  ],
}));
const SLOT_OPTIONS = ["Breakfast", "Snack 1", "Lunch", "Snack 2", "Dinner", "Pre-workout", "Post-workout"];

export default function CoachMeals() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);  // currently-edited program object
  const [activeDay, setActiveDay] = useState(0);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/coach/meal-programs");
      setList(data || []);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const startNew = () => {
    setEditing({ name: "", description: "", days: DEFAULT_DAYS });
    setActiveDay(0);
    setErr(""); setMsg("");
  };

  const setDay = (idx, updater) => setEditing((cur) => {
    const days = [...(cur.days || [])];
    days[idx] = updater(days[idx]);
    return { ...cur, days };
  });
  const setMeal = (di, mi, updater) => setDay(di, (d) => {
    const meals = [...(d.meals || [])];
    meals[mi] = updater(meals[mi]);
    return { ...d, meals };
  });

  const save = async () => {
    if (!editing.name?.trim()) { setErr("Template name is required."); return; }
    setSaving(true); setErr(""); setMsg("");
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description || "",
        days: (editing.days || []).map((d) => ({
          name: d.name,
          meals: (d.meals || []).map((m) => ({
            slot: m.slot, title: m.title || "", notes: m.notes || "",
            calories: m.calories ? Number(m.calories) : null,
            protein_g: m.protein_g ? Number(m.protein_g) : null,
          })),
        })),
      };
      if (editing.id) {
        await http.put(`/coach/meal-programs/${editing.id}`, payload);
        setMsg("Template updated.");
      } else {
        await http.post("/coach/meal-programs", payload);
        setMsg("Template created.");
      }
      setEditing(null);
      await refresh();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this template? Clients already using a clone of it will keep their plan.")) return;
    try {
      await http.delete(`/coach/meal-programs/${id}`);
      await refresh();
    } catch (e) { setErr(apiError(e)); }
  };

  if (editing) {
    const day = editing.days?.[activeDay];
    return (
      <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="coach-meals-editor">
        <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white text-xs uppercase tracking-[0.15em] flex items-center gap-1 mb-6">
          <ArrowLeft size={14} weight="bold" /> Back to library
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#FF3333] flex items-center justify-center"><ForkKnife size={20} weight="bold" color="#fff" /></div>
          <div>
            <div className="id-label">Meal template</div>
            <h1 className="font-oswald text-3xl font-bold uppercase tracking-wide leading-tight">
              {editing.id ? "Edit template" : "New template"}
            </h1>
          </div>
        </div>

        {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333] mb-4">{err}</div>}

        <div className="id-card mb-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5">
              <label className="id-label block mb-1">Template name</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="id-input" placeholder="e.g. Standard Cut 2200 kcal" data-testid="mp-name" />
            </div>
            <div className="md:col-span-6">
              <label className="id-label block mb-1">Description</label>
              <input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="id-input" placeholder="Who it's for, target cals, etc." data-testid="mp-desc" />
            </div>
            <div className="md:col-span-1 flex items-end">
              <button onClick={save} disabled={saving} className="id-btn-primary w-full" data-testid="mp-save">
                <FloppyDisk size={14} weight="bold" /> {saving ? "..." : "Save"}
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto mb-4">
          {(editing.days || []).map((d, i) => (
            <button key={i} onClick={() => setActiveDay(i)} data-testid={`mp-day-${i}`}
              className={`flex-shrink-0 px-4 py-2 border uppercase tracking-[0.15em] text-xs font-bold ${activeDay === i ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
              {d.name}
            </button>
          ))}
        </div>

        {day && (
          <div className="id-card">
            <div className="flex items-center justify-between mb-4">
              <input value={day.name} onChange={(e) => setDay(activeDay, (d) => ({ ...d, name: e.target.value }))} className="id-input max-w-[160px] font-oswald uppercase tracking-wide" />
              <button onClick={() => setDay(activeDay, (d) => ({ ...d, meals: [...(d.meals || []), { slot: "Snack 1", title: "", notes: "" }] }))} className="id-btn-secondary text-xs">
                <Plus size={12} weight="bold" /> Add meal
              </button>
            </div>
            <div className="space-y-3">
              {(day.meals || []).map((m, mi) => (
                <div key={mi} className="border border-[#262626] bg-[#0F0F0F] p-3">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                    <select value={m.slot} onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, slot: e.target.value }))} className="id-input md:col-span-2 uppercase text-xs tracking-[0.1em]">
                      {SLOT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input value={m.title} onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, title: e.target.value }))} className="id-input md:col-span-5" placeholder="e.g. 3 eggs + 2 toast + banana" />
                    <input type="number" value={m.calories || ""} onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, calories: e.target.value ? Number(e.target.value) : null }))} className="id-input md:col-span-2" placeholder="Calories" />
                    <input type="number" value={m.protein_g || ""} onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, protein_g: e.target.value ? Number(e.target.value) : null }))} className="id-input md:col-span-2" placeholder="Protein (g)" />
                    <button onClick={() => setDay(activeDay, (d) => ({ ...d, meals: d.meals.filter((_, i) => i !== mi) }))} className="md:col-span-1 border border-[#262626] hover:border-[#FF3333] hover:text-[#FF3333] text-zinc-500 px-2 py-2 flex items-center justify-center">
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                  <textarea value={m.notes || ""} onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, notes: e.target.value }))} rows={2} className="id-input mt-2" placeholder="Ingredients, prep, swaps..." />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="coach-meals-library">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="id-label">Library</div>
          <h1 className="font-oswald text-3xl font-bold uppercase tracking-wide">Meal templates</h1>
          <p className="text-zinc-400 font-manrope text-sm mt-1">Build once, clone to any client.</p>
        </div>
        <button onClick={startNew} className="id-btn-primary" data-testid="mp-new">
          <Plus size={14} weight="bold" /> New template
        </button>
      </div>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333] mb-4">{err}</div>}
      {msg && <div className="border border-emerald-700 bg-emerald-700/10 px-3 py-2 text-xs text-emerald-400 mb-4">{msg}</div>}

      {loading ? (
        <div className="id-label text-zinc-500 id-pulse">— Loading —</div>
      ) : list.length === 0 ? (
        <div className="id-card text-center py-12">
          <ForkKnife size={32} weight="bold" className="text-zinc-600 mx-auto mb-4" />
          <h2 className="id-h2 mb-2">No templates yet.</h2>
          <p className="text-zinc-400 font-manrope mb-6">Build a template once — then clone it onto every new client in 2 clicks.</p>
          <button onClick={startNew} className="id-btn-primary" data-testid="mp-new-empty">
            <Plus size={14} weight="bold" /> Build your first template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((t) => (
            <div key={t.id} className="id-card hover:border-[#FF3333] transition-colors" data-testid={`mp-item-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-oswald text-lg uppercase tracking-wide text-white">{t.name}</div>
                  <p className="text-zinc-400 text-xs font-manrope mt-1">{t.description || "—"}</p>
                  <div className="text-[10px] text-zinc-600 uppercase tracking-[0.15em] mt-2">
                    {(t.days || []).length} days · {(t.days || []).reduce((sum, d) => sum + (d.meals?.length || 0), 0)} meals
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => { setEditing(t); setActiveDay(0); }} className="id-btn-secondary text-xs" data-testid={`mp-edit-${t.id}`}>Edit</button>
                  <button onClick={() => remove(t.id)} className="text-xs text-zinc-500 hover:text-[#FF3333] px-2 py-1" data-testid={`mp-delete-${t.id}`}>
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
